// src/hooks/useFraudConfig.js
// Read/write hook for the Firestore document: fraud_config/default
// Exposes form state, field setter, validation, save, refresh, and toast.

import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ─── Known field definitions ──────────────────────────────────────────────────
// These are always rendered first, in a defined order with descriptions.
// Any additional numeric fields found in the document are rendered dynamically.
export const KNOWN_FIELDS = [
  {
    key: 'velocityLimitPerHour',
    section: 'Velocity Protection',
    label: 'Transactions allowed per hour',
    description:
      'Maximum successful payment attempts allowed per hour before velocity rule triggers.',
    icon: '⚡',
    accent: '#8B5CF6',
    accentBg: 'rgba(139,92,246,0.10)',
  },
  {
    key: 'destinationLimitPerDay',
    section: 'Destination Protection',
    label: 'Same destination limit per day',
    description:
      'Maximum successful topups to the same destination number per day.',
    icon: '📍',
    accent: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.10)',
  },
  {
    key: 'failedPaymentsLimit',
    section: 'Failed Payments',
    label: 'Failed payment limit',
    description:
      'Maximum refunded or failed transactions before the failed payment rule begins adding fraud score.',
    icon: '💳',
    accent: '#F59E0B',
    accentBg: 'rgba(245,158,11,0.10)',
  },
  {
    key: 'multipleCardsLimit',
    section: 'Multiple Cards',
    label: 'Cards per day',
    description:
      'Maximum unique card fingerprints allowed per user each day.',
    icon: '🔢',
    accent: '#EF4444',
    accentBg: 'rgba(239,68,68,0.10)',
  },
];

// Metadata keys that should never be exposed as editable fields
const META_KEYS = new Set([
  'updatedAt', 'createdAt', 'lastUpdated', 'modifiedAt',
  'modifiedBy', 'updatedBy', 'createdBy',
]);

// ─── Parse any Firestore timestamp ───────────────────────────────────────────
const parseTs = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (val.seconds) return new Date(val.seconds * 1000);
  return null;
};

// ─── Main Hook ────────────────────────────────────────────────────────────────
const useFraudConfig = () => {
  // Raw values from Firestore (number keyed object)
  const [savedValues, setSavedValues]     = useState({});
  // Working copy (string for input binding, converted on save)
  const [form, setForm]                   = useState({});
  // Unknown keys found in the doc beyond the 4 known fields
  const [unknownKeys, setUnknownKeys]     = useState([]);

  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState(null);
  const [lastUpdated, setLastUpdated]     = useState(null);
  const [toast, setToast]                 = useState(null);
  const [fieldErrors, setFieldErrors]     = useState({});

  // ── Show toast ───────────────────────────────────────────────────────────
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Fetch document ───────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'fraud_config', 'default'));
      if (!snap.exists()) {
        setError("Document 'fraud_config/default' does not exist yet. Add it in Firestore or save below to create it.");
        setForm({});
        setSavedValues({});
        setUnknownKeys([]);
        return;
      }
      const data = snap.data();
      console.log('[useFraudConfig] Loaded fraud_config/default:', data);

      // Separate known, unknown-numeric, and metadata fields
      const knownKeys = new Set(KNOWN_FIELDS.map((f) => f.key));
      const unk = [];
      const initForm = {};

      // Known fields — always initialise (default to '' if missing from doc)
      KNOWN_FIELDS.forEach((f) => {
        initForm[f.key] = data[f.key] != null ? String(data[f.key]) : '';
      });

      // Unknown fields — only numeric, non-metadata
      Object.entries(data).forEach(([k, v]) => {
        if (knownKeys.has(k) || META_KEYS.has(k)) return;
        if (typeof v === 'number') {
          unk.push(k);
          initForm[k] = String(v);
        }
      });

      setSavedValues(data);
      setForm(initForm);
      setUnknownKeys(unk);
      setLastUpdated(parseTs(data.updatedAt || data.lastUpdated || data.createdAt));
    } catch (err) {
      console.error('[useFraudConfig] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Set individual field ─────────────────────────────────────────────────
  const setField = useCallback((key, raw) => {
    setForm((f) => ({ ...f, [key]: raw }));
    // Clear error on edit
    if (fieldErrors[key]) {
      setFieldErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    }
  }, [fieldErrors]);

  // ── Validate all fields ──────────────────────────────────────────────────
  const validate = () => {
    const allKeys = [...KNOWN_FIELDS.map((f) => f.key), ...unknownKeys];
    const errs = {};
    allKeys.forEach((k) => {
      const raw = (form[k] || '').trim();
      if (raw === '') {
        errs[k] = 'Required';
      } else {
        const n = Number(raw);
        if (!Number.isInteger(n) || n <= 0) {
          errs[k] = 'Must be a positive integer';
        }
      }
    });
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save configuration ───────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const allKeys = [...KNOWN_FIELDS.map((f) => f.key), ...unknownKeys];
      const payload = { updatedAt: serverTimestamp() };
      allKeys.forEach((k) => {
        payload[k] = parseInt(form[k], 10);
      });

      await setDoc(doc(db, 'fraud_config', 'default'), payload, { merge: true });

      setSavedValues((prev) => ({ ...prev, ...payload }));
      setLastUpdated(new Date());
      showToast('success', 'Configuration saved successfully');
    } catch (err) {
      console.error('[useFraudConfig] Save error:', err);
      showToast('error', `Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, unknownKeys]);

  // Check if current form differs from saved values
  const isDirty = KNOWN_FIELDS.some((f) => String(savedValues[f.key] ?? '') !== (form[f.key] ?? '')) ||
    unknownKeys.some((k) => String(savedValues[k] ?? '') !== (form[k] ?? ''));

  return {
    form, setField,
    unknownKeys,
    savedValues,
    loading, saving, error,
    lastUpdated,
    fieldErrors,
    toast,
    isDirty,
    refresh,
    save,
  };
};

export default useFraudConfig;
